import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

export interface PortfolioTransaction {
  id: string;
  user_id: string;
  symbol: string;
  asset_name: string | null;
  transaction_type: string;
  quantity: number;
  price: number;
  total_value: number | null;
  fees: number | null;
  currency: string | null;
  notes: string | null;
  executed_at: string;
  created_at: string | null;
}

interface AddTransactionInput {
  symbol: string;
  transaction_type: 'buy' | 'sell' | 'dividend';
  quantity: number;
  price: number;
  executed_at?: string;
  notes?: string;
  asset_name?: string;
}

export function usePortfolioTransactions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const transactionsQuery = useQuery({
    queryKey: ['portfolio-transactions', user?.id],
    queryFn: async (): Promise<PortfolioTransaction[]> => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('portfolio_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('executed_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data || []) as PortfolioTransaction[];
    },
    enabled: !!user,
    staleTime: 30000,
  });

  const addTransaction = useMutation({
    mutationFn: async (input: AddTransactionInput) => {
      if (!user) throw new Error('Not authenticated');

      // Insert transaction
      const { error: txError } = await supabase.from('portfolio_transactions').insert({
        user_id: user.id,
        symbol: input.symbol,
        transaction_type: input.transaction_type,
        quantity: input.quantity,
        price: input.price,
        executed_at: input.executed_at || new Date().toISOString(),
        notes: input.notes || null,
        asset_name: input.asset_name || null,
      });
      if (txError) throw txError;

      // Update holdings for buy/sell
      if (input.transaction_type === 'buy') {
        // Check if holding exists
        const { data: existing } = await supabase
          .from('portfolio_holdings')
          .select('id, quantity, average_price')
          .eq('user_id', user.id)
          .eq('symbol', input.symbol)
          .is('broker_connection_id', null)
          .maybeSingle();

        if (existing) {
          const oldQty = Number(existing.quantity);
          const oldAvg = Number(existing.average_price);
          const newQty = oldQty + input.quantity;
          const newAvg = (oldAvg * oldQty + input.price * input.quantity) / newQty;

          await supabase.from('portfolio_holdings').update({
            quantity: newQty,
            average_price: newAvg,
          }).eq('id', existing.id);
        } else {
          // Look up sector
          const { data: sc } = await supabase
            .from('sector_classifications')
            .select('sector, asset_type')
            .eq('symbol', input.symbol)
            .maybeSingle();

          await supabase.from('portfolio_holdings').insert({
            user_id: user.id,
            symbol: input.symbol,
            asset_type: sc?.asset_type || 'stock',
            quantity: input.quantity,
            average_price: input.price,
            current_price: input.price,
            sector: sc?.sector || 'Other',
            source: 'manual',
            asset_name: input.asset_name || null,
          });
        }
      } else if (input.transaction_type === 'sell') {
        const { data: existing } = await supabase
          .from('portfolio_holdings')
          .select('id, quantity')
          .eq('user_id', user.id)
          .eq('symbol', input.symbol)
          .is('broker_connection_id', null)
          .maybeSingle();

        if (existing) {
          const remainingQty = Number(existing.quantity) - input.quantity;
          if (remainingQty <= 0) {
            await supabase.from('portfolio_holdings').delete().eq('id', existing.id);
          } else {
            await supabase.from('portfolio_holdings').update({ quantity: remainingQty }).eq('id', existing.id);
          }
        }
      }
      // Dividend: just logged, no holdings update
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-holdings'] });
      toast({ title: 'Transaction recorded', description: 'Your transaction has been logged.' });
    },
    onError: (err: any) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  return {
    transactions: transactionsQuery.data || [],
    isLoading: transactionsQuery.isLoading,
    addTransaction,
  };
}
